import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { ButtonTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MESSAGING_TEMPLATES_URL } from "@constants/routes";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { MessagingTemplateDetailsTabTypes } from "@enums/messaging-template-details-tab-types";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { navigateToUtil } from "@utils/navigation-util";
import { getMessagingTemplateValidationErrors } from "@utils/validation/messaging-template/messaging-template-validation-util";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { MessagingTemplateGeneralInfoTab } from "@containers/messaging-template-details/messaging-template-details-component/messaging-template-general-info-tab/messaging-template-general-info-tab";
import { MessagingTemplateContentTab } from "@containers/messaging-template-details/messaging-template-details-component/messaging-template-content-tab/messaging-template-content-tab";
import styles from "./messaging-template-details-component.less";
import { IDWSession } from "@idw-types/data/idw-session";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import isEqual from "lodash/isEqual";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { ModalTypes } from "@enums/modal-types";
import { ConfirmModal } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/confirm-modal/confirm-modal";
import { useModalContext } from "@context/use-modal-context";

type MessagingTemplateDetailsComponentProps = {
    session: IDWSession;
    initialMessagingTemplate: FormattedMessagingTemplate;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    handleSave: (messagingTemplateState: FormattedMessagingTemplate) => Promise<FormattedMessagingTemplate>;
};

export const MessagingTemplateDetailsComponent = (props: MessagingTemplateDetailsComponentProps) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { session, initialMessagingTemplate, axnCompanies, axnRelyingParties, handleSave } = props;
    const [messagingTemplateState, setMessagingTemplateState] = useState<FormattedMessagingTemplate>(initialMessagingTemplate);
    const modalContext = useModalContext();

    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(LocaleTypes.ENGLISH);
    const [, setCurrentTab] = useState(MessagingTemplateDetailsTabTypes.GENERAL_INFO);
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);

    const resource = resources.pages.messagingTemplateDetails;

    const [requestInProgress, setRequestInProgress] = useState(false);

    const stateIsUnchanged = useMemo(() => {
        return isEqual(initialMessagingTemplate, messagingTemplateState);
    }, [initialMessagingTemplate, messagingTemplateState]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-messaging-template-changes",
        modalType: ModalTypes.DiscardMessagingTemplateChanges,
        stateIsUnchanged
    });

    useEffect(() => {
        setMessagingTemplateState(initialMessagingTemplate);
    }, [initialMessagingTemplate])

    const validationErrors = useMemo(() => {
        return getMessagingTemplateValidationErrors(messagingTemplateState);
    }, [messagingTemplateState]);

    const onCloseClick = () => {
        navigateToUtil(MESSAGING_TEMPLATES_URL, navigate, routerLocation.pathname);
    };

    const renderConfirmSaveMessagingTemplateModal = (
        confirmMessage: string | React.ReactNode,
        onSubmit: (messagingTemplateDetailsState: FormattedMessagingTemplate) => Promise<void>
    ) => {
        const renderModal = (onModalClose: () => void) => {
            return (
                <ConfirmModal
                    id={"confirm-save-messaging-template"}
                    title={resources.modals.updateMessagingTemplate.title}
                    confirmMessage={confirmMessage}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={resources.common.buttons.save.label}
                    onSubmit={null}
                    onSubmitAsync={onSubmit}
                    onModalClose={onModalClose}
                    customProps={messagingTemplateState}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    };

    const onSubmit = async () => {
        setRequestInProgress(true);
        await handleSave(messagingTemplateState);
        setRequestInProgress(false);
    };

    const confirmMessage = useMemo(() => {
        return (
            <div>
                <p>{resources.modals.updateMessagingTemplate.confirmMessageTitle}</p>
                <p>{resources.modals.updateMessagingTemplate.confirmMessageDescription}</p>
            </div>
        );
    }, []);

    const onClickSave = async () => {
        renderConfirmSaveMessagingTemplateModal(confirmMessage, onSubmit);
    };

    return (
        <div className={styles.messagingTemplateDetailsComponent}>
            <div className={styles.headerContainer}>
                <span className={styles.templateName}>
                    {`${messagingTemplateState.name}`}
                </span>
                <div className={styles.buttonContainer}>
                    <Button
                        id="messaging-template-details-close-button"
                        onClick={onCloseClick}
                        variant={"outline-secondary"}
                    >
                        {resources.common.buttons.close.label}
                    </Button>
                    {initialMessagingTemplate.permissions.canModify && (
                        <ButtonWithSpinner
                            id="messaging-template-detail-save"
                            disabled={stateIsUnchanged || requestInProgress || validationErrors.count > 0}
                            hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                            label={resources.common.buttons.save.label}
                            type={ButtonTypes.SUBMIT}
                            onClick={onClickSave}
                            variant={"primary"}
                        />
                    )}
                </div>
            </div>
            <TabContainer>
                {/* @ts-ignore */}
                <Tabs id="messaging-template-details" fill={true} unmountOnExit={true} onSelect={setCurrentTab}>
                    <Tab eventKey={MessagingTemplateDetailsTabTypes.GENERAL_INFO} title={validationErrors.generalInfo.tabTitle}>
                        <MessagingTemplateGeneralInfoTab
                            isDisabled={!initialMessagingTemplate.permissions.canModify}
                            session={session}
                            axnCompanies={axnCompanies}
                            axnRelyingParties={axnRelyingParties}
                            state={messagingTemplateState}
                            labelWidth={labelWidth}
                            validationErrors={validationErrors.generalInfo}
                            setMessagingTemplateState={setMessagingTemplateState}
                        />
                    </Tab>
                    {initialMessagingTemplate.permissions.canViewBranding && (
                        <Tab eventKey={MessagingTemplateDetailsTabTypes.CONTENT} title={validationErrors.contentAndVariables.tabTitle}>
                            <div>
                                <ContentPageHeader title={resource.tabs.content.title} />
                                <ContentPageBody isMaxWidth={true}>
                                    <MessagingTemplateContentTab
                                        isDisabled={!initialMessagingTemplate.permissions.canModify || !initialMessagingTemplate.permissions.canModifyBranding}
                                        state={messagingTemplateState}
                                        labelWidth={labelWidth}
                                        selectedLanguage={selectedLanguage}
                                        contentValidationErrors={validationErrors.content}
                                        variablesValidationErrors={validationErrors.variables}
                                        setSelectedLanguage={setSelectedLanguage}
                                        setMessagingTemplateState={setMessagingTemplateState}
                                    />
                                </ContentPageBody>
                            </div>
                        </Tab>
                    )}
                </Tabs>
            </TabContainer>
        </div>
    );
};